import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Crown,
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Zap,
  Lock,
  UserRound,
  Mail,
  Gift,
  KeyRound,
  Smartphone,
  CheckCircle2,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { apiRequest } from "../api";

// ============================================================
// STANDALONE COMPONENTS (moved outside Login to prevent
// remount-on-every-keystroke, which was closing the mobile
// keyboard after each character)
// ============================================================

function BackgroundFX() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-[#05040D]" />

      <div
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-25 blur-[110px] animate-pulse"
        style={{
          background: "radial-gradient(circle, #6D5DF5 0%, transparent 70%)",
        }}
      />

      <div
        className="absolute top-[25%] -right-48 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px]"
        style={{
          background: "radial-gradient(circle, #D946A8 0%, transparent 70%)",
        }}
      />

      <div
        className="absolute -bottom-52 left-[20%] w-[550px] h-[550px] rounded-full opacity-15 blur-[120px]"
        style={{
          background: "radial-gradient(circle, #22D3EE 0%, transparent 70%)",
        }}
      />

      <div
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
          maskImage: "radial-gradient(circle at center, black, transparent 80%)",
        }}
      />

      <div
        className="absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            "radial-gradient(1px 1px at 15% 20%, rgba(255,255,255,.45), transparent), radial-gradient(1px 1px at 80% 25%, rgba(255,255,255,.35), transparent), radial-gradient(1px 1px at 35% 75%, rgba(255,255,255,.3), transparent), radial-gradient(1px 1px at 90% 80%, rgba(255,255,255,.3), transparent), radial-gradient(1px 1px at 60% 45%, rgba(255,255,255,.2), transparent)",
        }}
      />
    </div>
  );
}

function InputField({
  icon: Icon,
  type = "text",
  placeholder,
  value,
  onChange,
  autoComplete,
  inputMode,
  maxLength,
}) {
  return (
    <div className="group relative">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-[#777A9A] group-focus-within:text-[#A99FFF] transition-colors duration-200">
        <Icon size={17} strokeWidth={1.8} />
      </div>

      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        className="
          w-full h-[52px]
          bg-white/[0.035]
          border border-white/[0.09]
          rounded-2xl
          pl-11 pr-4
          text-sm text-white
          placeholder:text-[#666A88]
          outline-none
          transition-all duration-300
          hover:border-white/[0.16]
          hover:bg-white/[0.045]
          focus:border-[#8B7FE8]/80
          focus:bg-white/[0.06]
          focus:ring-4
          focus:ring-[#8B7FE8]/10
          focus:shadow-[0_0_30px_-12px_rgba(139,127,232,.8)]
        "
      />
    </div>
  );
}

function PasswordField({ value, onChange, show, onToggle, placeholder, autoComplete }) {
  return (
    <div className="group relative">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-[#777A9A] group-focus-within:text-[#A99FFF] transition-colors duration-200">
        <Lock size={17} strokeWidth={1.8} />
      </div>

      <input
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        minLength={6}
        autoComplete={autoComplete}
        className="
          w-full h-[52px]
          bg-white/[0.035]
          border border-white/[0.09]
          rounded-2xl
          pl-11 pr-12
          text-sm text-white
          placeholder:text-[#666A88]
          outline-none
          transition-all duration-300
          hover:border-white/[0.16]
          hover:bg-white/[0.045]
          focus:border-[#8B7FE8]/80
          focus:bg-white/[0.06]
          focus:ring-4
          focus:ring-[#8B7FE8]/10
          focus:shadow-[0_0_30px_-12px_rgba(139,127,232,.8)]
        "
      />

      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? "Hide password" : "Show password"}
        className="
          absolute right-3 top-1/2 -translate-y-1/2
          h-9 w-9
          flex items-center justify-center
          rounded-xl
          text-[#777A9A]
          hover:text-white
          hover:bg-white/10
          transition-all duration-200
        "
      >
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center justify-center gap-3">
      <div
        className="
          relative
          w-14 h-14
          rounded-[18px]
          bg-gradient-to-br from-[#F7D477] via-[#D946A8] to-[#725CF6]
          flex items-center justify-center
          shadow-[0_15px_45px_-10px_rgba(217,70,168,.65)]
        "
      >
        <div className="absolute inset-[1px] rounded-[17px] bg-gradient-to-br from-white/20 to-transparent" />

        <Crown size={27} strokeWidth={2} className="relative text-[#0A0912]" />
      </div>

      <div className="text-left">
        <div className="font-black text-[22px] tracking-[-0.04em] bg-gradient-to-r from-white via-white to-[#B9B4E8] bg-clip-text text-transparent">
          VIP EARN
        </div>

        <div className="text-[9px] font-semibold text-[#8B7FE8] tracking-[0.32em]">
          SECURE ACCOUNT
        </div>
      </div>
    </div>
  );
}

function ErrorBox({ error }) {
  return error ? (
    <div className="flex items-start gap-3 rounded-2xl border border-[#E5566B]/25 bg-[#E5566B]/[0.08] px-4 py-3.5 mb-4">
      <div className="shrink-0 w-7 h-7 rounded-lg bg-[#E5566B]/10 flex items-center justify-center">
        <AlertCircle size={15} className="text-[#F07889]" />
      </div>

      <p className="text-xs leading-5 text-[#F49AAA] pt-1">{error}</p>
    </div>
  ) : null;
}

function GradientButton({ children, disabled, type = "submit", onClick }) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className="
        relative
        overflow-hidden
        w-full
        h-[52px]
        rounded-2xl
        bg-gradient-to-r from-[#5B4CF5] via-[#8B7FE8] to-[#D946A8]
        text-sm
        font-bold
        text-white
        flex items-center justify-center gap-2
        shadow-[0_15px_35px_-15px_rgba(139,127,232,.8)]
        hover:shadow-[0_20px_45px_-15px_rgba(217,70,168,.7)]
        hover:-translate-y-[1px]
        active:translate-y-0
        disabled:opacity-40
        disabled:cursor-not-allowed
        disabled:hover:translate-y-0
        transition-all duration-300
      "
    >
      <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 -translate-x-full hover:translate-x-full transition-transform duration-700" />
      <span className="relative flex items-center justify-center gap-2">{children}</span>
    </button>
  );
}

// ============================================================
// MAIN LOGIN COMPONENT
// ============================================================

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref");

  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [manualReferralCode, setManualReferralCode] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  const [authCode, setAuthCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const reason = searchParams.get("reason");

    if (reason === "suspended") {
      setError("Your account is suspended.");
    }
  }, [searchParams]);

  const [twoFactorStep, setTwoFactorStep] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);

  // ==========================================================
  // FORGOT PASSWORD STATE
  // ==========================================================

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState("verify");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState("");

  // ==========================================================
  // SAVE LOGIN
  // ==========================================================

  const saveLogin = (data) => {
    if (!data || !data.token || !data.user) {
      throw new Error("Invalid login response.");
    }

    localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));

    const isAdmin = data.user.is_admin === true || Number(data.user.is_admin) === 1;

    navigate(isAdmin ? "/admin" : "/");
  };

  const resetTwoFactor = () => {
    setTwoFactorStep(false);
    setTwoFactorSetup(null);
    setTwoFactorUserId(null);
    setAuthCode("");
  };

  const resetForgotPasswordFlow = () => {
    setShowForgotPassword(false);
    setForgotStep("verify");
    setForgotEmail("");
    setForgotCode("");
    setResetToken(null);
    setNewPassword("");
    setConfirmNewPassword("");
    setForgotSuccess("");
    setError("");
  };

  // ==========================================================
  // FORGOT PASSWORD HANDLERS
  // ==========================================================

  const handleForgotVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const cleanEmail = forgotEmail.trim().toLowerCase();

      if (!cleanEmail) {
        throw new Error("Please enter your email.");
      }

      if (!/^\d{6}$/.test(forgotCode)) {
        throw new Error("Google Authenticator code must be 6 digits.");
      }

      const data = await apiRequest("/auth/forgot-password/verify", {
        method: "POST",
        body: JSON.stringify({
          email: cleanEmail,
          code: forgotCode,
        }),
      });

      setResetToken(data.resetToken);
      setForgotStep("reset");
      setForgotCode("");
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (newPassword.length < 6) {
        throw new Error("Password must be at least 6 characters.");
      }

      if (newPassword !== confirmNewPassword) {
        throw new Error("Passwords do not match.");
      }

      const data = await apiRequest("/auth/forgot-password/reset", {
        method: "POST",
        body: JSON.stringify({
          resetToken,
          newPassword,
        }),
      });

      setForgotSuccess(
        data.message || "Password updated successfully. You can now log in."
      );

      setNewPassword("");
      setConfirmNewPassword("");
      setResetToken(null);
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // HANDLE SUBMIT
  // ==========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isRegister && !twoFactorStep) {
        const cleanName = name.trim();
        const cleanEmail = email.trim().toLowerCase();

        if (!cleanName) {
          throw new Error("Please enter your name.");
        }

        if (!/^[A-Za-zÀ-ÿ\s'-]+$/.test(cleanName)) {
          throw new Error(
            "Name can only contain letters, spaces, apostrophes and hyphens."
          );
        }

        if (!cleanEmail) {
          throw new Error("Please enter your email.");
        }

        if (password.length < 6) {
          throw new Error("Password must be at least 6 characters.");
        }

        const data = await apiRequest("/auth/register", {
          method: "POST",
          body: JSON.stringify({
            name: cleanName,
            email: cleanEmail,
            password,
            referralCode: referralCode || manualReferralCode.trim() || null,
          }),
        });

        if (data && data.twoFactorRequired && data.twoFactorSetup && data.userId) {
          setTwoFactorSetup(data.twoFactorSetup);
          setTwoFactorUserId(data.userId);
          setTwoFactorStep(true);
          setAuthCode("");
          return;
        }

        throw new Error(
          "Account created, but Google Authenticator setup was not returned."
        );
      }

      if (isRegister && twoFactorStep) {
        if (!/^\d{6}$/.test(authCode)) {
          throw new Error("Google Authenticator code must be 6 digits.");
        }

        if (!twoFactorUserId) {
          throw new Error("User verification session is missing.");
        }

        const data = await apiRequest("/auth/register/verify-2fa", {
          method: "POST",
          body: JSON.stringify({
            userId: twoFactorUserId,
            code: authCode,
          }),
        });

        saveLogin(data);
        return;
      }

      if (!isRegister && !twoFactorStep) {
        const cleanEmail = email.trim().toLowerCase();

        if (!cleanEmail) {
          throw new Error("Please enter your email.");
        }

        if (!password) {
          throw new Error("Please enter your password.");
        }

        let data;

        try {
          data = await apiRequest("/auth/login", {
            method: "POST",
            body: JSON.stringify({
              email: cleanEmail,
              password,
            }),
          });
        } catch (err) {
          if (err?.status === 401 && err?.data?.twoFactorRequired) {
            setTwoFactorSetup(null);
            setTwoFactorUserId(err.data.userId || null);
            setTwoFactorStep(true);
            setAuthCode("");
            setError("");
            return;
          }

          if (
            err?.status === 401 &&
            err?.data?.twoFactorSetupRequired &&
            err?.data?.twoFactorSetup
          ) {
            setTwoFactorSetup(err.data.twoFactorSetup);
            setTwoFactorUserId(err.data.userId || null);
            setTwoFactorStep(true);
            setAuthCode("");
            setError("");
            return;
          }

          throw err;
        }

        if (data && data.twoFactorSetupRequired && data.twoFactorSetup && data.userId) {
          setTwoFactorSetup(data.twoFactorSetup);
          setTwoFactorUserId(data.userId);
          setTwoFactorStep(true);
          setAuthCode("");
          return;
        }

        if (data && data.twoFactorRequired) {
          setTwoFactorSetup(null);
          setTwoFactorUserId(data.userId || null);
          setTwoFactorStep(true);
          setAuthCode("");
          return;
        }

        saveLogin(data);
        return;
      }

      if (!isRegister && twoFactorStep) {
        if (!/^\d{6}$/.test(authCode)) {
          throw new Error("Google Authenticator code must be 6 digits.");
        }

        const cleanEmail = email.trim().toLowerCase();

        if (twoFactorSetup && twoFactorUserId) {
          const data = await apiRequest("/auth/setup-2fa/verify", {
            method: "POST",
            body: JSON.stringify({
              userId: twoFactorUserId,
              code: authCode,
            }),
          });

          saveLogin(data);
          return;
        }

        const data = await apiRequest("/auth/login", {
          method: "POST",
          body: JSON.stringify({
            email: cleanEmail,
            password,
            twoFactorCode: authCode,
          }),
        });

        saveLogin(data);
        return;
      }
    } catch (err) {
      console.error("AUTH ERROR:", err);
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchMode = () => {
    setIsRegister((current) => !current);
    setName("");
    setEmail("");
    setPassword("");
    setAuthCode("");
    setManualReferralCode("");
    setError("");
    resetTwoFactor();
  };

  const handleBack = () => {
    resetTwoFactor();
    setError("");
  };

  // ==========================================================
  // FORGOT PASSWORD SCREEN
  // ==========================================================

  if (showForgotPassword) {
    return (
      <div className="min-h-screen w-full bg-[#05040D] text-white flex items-center justify-center p-4 relative overflow-hidden">
        <BackgroundFX />

        <div className="w-full max-w-[430px] relative z-10">
          <div className="mb-7">
            <Brand />
          </div>

          <div className="rounded-[30px] border border-white/[0.1] bg-white/[0.045] backdrop-blur-3xl p-5 sm:p-7 shadow-[0_30px_100px_-30px_rgba(0,0,0,.9)]">
            {forgotSuccess ? (
              <>
                <div className="flex justify-center mb-5">
                  <div className="w-16 h-16 rounded-2xl bg-[#3FBF8F]/10 border border-[#3FBF8F]/20 flex items-center justify-center">
                    <CheckCircle2 size={30} className="text-[#3FBF8F]" />
                  </div>
                </div>

                <div className="text-center">
                  <h1 className="text-2xl font-bold mb-2">Password Updated</h1>

                  <p className="text-sm text-[#969AB7] leading-6 mb-6">{forgotSuccess}</p>
                </div>

                <GradientButton type="button" onClick={resetForgotPasswordFlow}>
                  <ArrowLeft size={17} />
                  Back to Log in
                </GradientButton>
              </>
            ) : forgotStep === "verify" ? (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-2xl bg-[#8B7FE8]/10 border border-[#8B7FE8]/20 flex items-center justify-center">
                    <KeyRound size={20} className="text-[#A99FFF]" />
                  </div>

                  <div>
                    <h1 className="text-xl font-bold">Reset Password</h1>

                    <p className="text-[11px] text-[#737794] mt-0.5">
                      Verify your account securely
                    </p>
                  </div>
                </div>

                <p className="text-xs leading-5 text-[#9599B5] mb-5">
                  Enter your email and the 6-digit code from your Google Authenticator app.
                </p>

                <ErrorBox error={error} />

                <form onSubmit={handleForgotVerify} className="space-y-3">
                  <InputField
                    icon={Mail}
                    type="email"
                    placeholder="Email address"
                    value={forgotEmail}
                    onChange={(e) => {
                      setForgotEmail(e.target.value);
                      setError("");
                    }}
                    autoComplete="email"
                  />

                  <div className="relative">
                    <Smartphone
                      size={17}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[#777A9A]"
                    />

                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="6-digit Authenticator code"
                      value={forgotCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 6);

                        setForgotCode(value);
                        setError("");
                      }}
                      required
                      className="
                        w-full h-[58px]
                        bg-white/[0.035]
                        border border-white/[0.09]
                        rounded-2xl
                        pl-11 pr-4
                        text-center
                        text-xl
                        font-semibold
                        tracking-[0.4em]
                        text-white
                        placeholder:text-[#666A88]
                        placeholder:text-xs
                        placeholder:tracking-normal
                        outline-none
                        focus:border-[#8B7FE8]/80
                        focus:bg-white/[0.06]
                        focus:ring-4
                        focus:ring-[#8B7FE8]/10
                        transition-all
                      "
                    />
                  </div>

                  <GradientButton disabled={loading || forgotCode.length !== 6}>
                    {loading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Verify Code
                        <ArrowRight size={17} />
                      </>
                    )}
                  </GradientButton>
                </form>

                <button
                  type="button"
                  onClick={resetForgotPasswordFlow}
                  disabled={loading}
                  className="w-full text-center text-xs text-[#8589A8] mt-5 hover:text-white transition disabled:opacity-50"
                >
                  Back to Log in
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-2xl bg-[#D946A8]/10 border border-[#D946A8]/20 flex items-center justify-center">
                    <Lock size={19} className="text-[#EA70BC]" />
                  </div>

                  <div>
                    <h1 className="text-xl font-bold">Set New Password</h1>

                    <p className="text-[11px] text-[#737794] mt-0.5">
                      Create a secure new password
                    </p>
                  </div>
                </div>

                <p className="text-xs leading-5 text-[#9599B5] mb-5">
                  Choose a new password for your account.
                </p>

                <ErrorBox error={error} />

                <form onSubmit={handleForgotReset} className="space-y-3">
                  <PasswordField
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setError("");
                    }}
                    show={showNewPassword}
                    onToggle={() => setShowNewPassword((v) => !v)}
                    placeholder="New password"
                    autoComplete="new-password"
                  />

                  <PasswordField
                    value={confirmNewPassword}
                    onChange={(e) => {
                      setConfirmNewPassword(e.target.value);
                      setError("");
                    }}
                    show={showNewPassword}
                    onToggle={() => setShowNewPassword((v) => !v)}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                  />

                  <GradientButton disabled={loading}>
                    {loading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        Update Password
                        <ArrowRight size={17} />
                      </>
                    )}
                  </GradientButton>
                </form>

                <button
                  type="button"
                  onClick={resetForgotPasswordFlow}
                  disabled={loading}
                  className="w-full text-center text-xs text-[#8589A8] mt-5 hover:text-white transition disabled:opacity-50"
                >
                  Cancel
                </button>
              </>
            )}
          </div>

          <p className="text-center text-[10px] text-[#555974] mt-5">
            Secure password recovery · Google Authenticator protected
          </p>
        </div>
      </div>
    );
  }

  // ==========================================================
  // MAIN LOGIN / REGISTER
  // ==========================================================

  return (
    <div className="min-h-screen w-full bg-[#05040D] text-white flex items-center justify-center p-4 relative overflow-hidden">
      <BackgroundFX />

      <div className="w-full max-w-[980px] relative z-10">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 items-center">
          {/* ==================================================
              LEFT BRAND PANEL
          ================================================== */}

          <div className="hidden lg:block px-8">
            <div className="mb-10">
              <Brand />
            </div>

            <div className="max-w-md">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#8B7FE8]/20 bg-[#8B7FE8]/[0.08] text-[10px] font-semibold text-[#AFA7FF] mb-5">
                <Sparkles size={12} />
                SECURE EARNING PLATFORM
              </div>

              <h2 className="text-4xl xl:text-5xl font-black tracking-[-0.05em] leading-[1.05]">
                Your account.
                <br />
                <span className="bg-gradient-to-r from-[#9D91FF] via-[#D946A8] to-[#F5C563] bg-clip-text text-transparent">
                  Your control.
                </span>
              </h2>

              <p className="text-sm leading-6 text-[#8589A7] mt-5 max-w-sm">
                A secure account experience built with modern authentication and
                two-step protection.
              </p>

              <div className="space-y-3 mt-8">
                {[
                  {
                    icon: ShieldCheck,
                    title: "Protected Authentication",
                    text: "Your account is protected with secure authentication.",
                  },
                  {
                    icon: Smartphone,
                    title: "Two-Step Verification",
                    text: "Google Authenticator adds another layer of security.",
                  },
                  {
                    icon: Zap,
                    title: "Fast & Simple",
                    text: "Quick access with a clean and responsive experience.",
                  },
                ].map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="
                        flex items-center gap-4
                        p-4 rounded-2xl
                        border border-white/[0.06]
                        bg-white/[0.025]
                        hover:bg-white/[0.045]
                        hover:border-white/[0.1]
                        transition-all duration-300
                      "
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#8B7FE8]/10 border border-[#8B7FE8]/10 flex items-center justify-center shrink-0">
                        <Icon size={18} className="text-[#A99FFF]" />
                      </div>

                      <div>
                        <div className="text-xs font-semibold text-white">
                          {item.title}
                        </div>

                        <div className="text-[10px] text-[#696D89] mt-1 leading-4">
                          {item.text}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ==================================================
              AUTH CARD
          ================================================== */}

          <div className="w-full max-w-[470px] mx-auto lg:ml-auto">
            <div className="lg:hidden mb-7">
              <Brand />
            </div>

            <div
              className="
                relative
                rounded-[30px]
                border border-white/[0.1]
                bg-white/[0.045]
                backdrop-blur-3xl
                p-5 sm:p-7
                shadow-[0_35px_100px_-30px_rgba(0,0,0,.9)]
                overflow-hidden
              "
            >
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-[#8B7FE8]/70 to-transparent" />

              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#8B7FE8]/10 rounded-full blur-3xl pointer-events-none" />

              {twoFactorStep ? (
                <>
                  {/* ==================================================
                      2FA
                  ================================================== */}

                  <div className="relative">
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={loading}
                      className="
                        absolute right-0 top-0
                        w-9 h-9
                        rounded-xl
                        border border-white/[0.08]
                        bg-white/[0.03]
                        flex items-center justify-center
                        text-[#747895]
                        hover:text-white
                        hover:bg-white/[0.08]
                        transition
                      "
                    >
                      <ArrowLeft size={16} />
                    </button>

                    <div className="flex items-center gap-3 mb-6 pr-12">
                      <div className="w-12 h-12 rounded-2xl bg-[#8B7FE8]/10 border border-[#8B7FE8]/20 flex items-center justify-center">
                        <Smartphone size={21} className="text-[#A99FFF]" />
                      </div>

                      <div>
                        <h1 className="text-xl font-bold">Google Authenticator</h1>

                        <p className="text-[10px] text-[#727690] mt-1">
                          {twoFactorSetup
                            ? "Set up your secure verification"
                            : "Enter your verification code"}
                        </p>
                      </div>
                    </div>

                    <p className="text-xs leading-5 text-[#9296B2] mb-5">
                      {twoFactorSetup
                        ? "Scan the QR code with Google Authenticator, then enter the 6-digit code."
                        : "Enter the 6-digit code from your Google Authenticator app."}
                    </p>

                    <ErrorBox error={error} />

                    {twoFactorSetup?.qrCode && (
                      <div className="mb-6">
                        <div className="relative flex justify-center">
                          <div className="absolute inset-8 bg-[#8B7FE8]/20 blur-3xl rounded-full" />

                          <div className="relative bg-white p-3 rounded-3xl shadow-[0_20px_60px_-20px_rgba(139,127,232,.7)]">
                            <img
                              src={twoFactorSetup.qrCode}
                              alt="Google Authenticator QR Code"
                              className="w-44 h-44 sm:w-48 sm:h-48"
                            />
                          </div>
                        </div>

                        {twoFactorSetup.secret && (
                          <div className="mt-5">
                            <div className="flex items-center justify-center gap-2 mb-2">
                              <KeyRound size={12} className="text-[#777B99]" />

                              <p className="text-[10px] uppercase tracking-[0.15em] text-[#777B99]">
                                Manual setup key
                              </p>
                            </div>

                            <div className="bg-black/20 border border-white/[0.08] rounded-2xl p-3 text-center break-all text-[11px] tracking-wider text-[#D8DEEA]">
                              {twoFactorSetup.secret}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                          <KeyRound size={17} className="text-[#777A9A]" />
                        </div>

                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          placeholder="6-digit code"
                          value={authCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "").slice(0, 6);

                            setAuthCode(value);
                            setError("");
                          }}
                          required
                          autoFocus
                          className="
                            w-full h-[62px]
                            bg-white/[0.035]
                            border border-white/[0.09]
                            rounded-2xl
                            pl-11 pr-4
                            text-center
                            text-2xl
                            font-bold
                            tracking-[0.45em]
                            text-white
                            placeholder:text-[#656983]
                            placeholder:text-xs
                            placeholder:tracking-normal
                            outline-none
                            focus:border-[#8B7FE8]/80
                            focus:ring-4
                            focus:ring-[#8B7FE8]/10
                            transition-all
                          "
                        />
                      </div>

                      <GradientButton disabled={loading || authCode.length !== 6}>
                        {loading ? (
                          <>
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            Verify & Continue
                            <ArrowRight size={17} />
                          </>
                        )}
                      </GradientButton>
                    </form>

                    <div className="flex items-center justify-center gap-2 mt-5 text-[10px] text-[#62667F]">
                      <ShieldCheck size={13} />
                      Two-step verification enabled
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* ==================================================
                      LOGIN / REGISTER
                  ================================================== */}

                  <div className="relative">
                    <div className="flex items-center justify-between gap-4 mb-6">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[9px] uppercase tracking-[0.2em] text-[#777B99]">
                            {isRegister ? "New account" : "Secure login"}
                          </span>

                          <span className="w-1 h-1 rounded-full bg-[#8B7FE8]" />
                        </div>

                        <h1 className="text-2xl sm:text-[27px] font-black tracking-[-0.04em] bg-gradient-to-r from-white via-white to-[#BBB6E7] bg-clip-text text-transparent">
                          {isRegister ? "Create Your Account" : "Welcome Back"}
                        </h1>

                        <p className="text-xs text-[#8589A6] mt-2 leading-5 max-w-[330px]">
                          {isRegister
                            ? "Join VIP Earn and start earning with secure account protection."
                            : "Log in to continue to your VIP Earn account."}
                        </p>
                      </div>

                      <div className="hidden sm:flex w-11 h-11 rounded-2xl bg-white/[0.04] border border-white/[0.08] items-center justify-center shrink-0">
                        {isRegister ? (
                          <Crown size={19} className="text-[#DFA8FF]" />
                        ) : (
                          <Lock size={18} className="text-[#A99FFF]" />
                        )}
                      </div>
                    </div>

                    {/* MODE SWITCH */}
                    <div className="grid grid-cols-2 p-1 rounded-2xl bg-black/20 border border-white/[0.06] mb-5">
                      <button
                        type="button"
                        onClick={() => {
                          if (isRegister) handleSwitchMode();
                        }}
                        disabled={loading}
                        className={`
                          h-9 rounded-xl text-[11px] font-semibold transition-all
                          ${
                            !isRegister
                              ? "bg-white/[0.09] text-white shadow-sm"
                              : "text-[#6F738E] hover:text-white"
                          }
                        `}
                      >
                        Log In
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          if (!isRegister) handleSwitchMode();
                        }}
                        disabled={loading}
                        className={`
                          h-9 rounded-xl text-[11px] font-semibold transition-all
                          ${
                            isRegister
                              ? "bg-white/[0.09] text-white shadow-sm"
                              : "text-[#6F738E] hover:text-white"
                          }
                        `}
                      >
                        Create Account
                      </button>
                    </div>

                    {isRegister && referralCode && (
                      <div className="flex items-center gap-3 rounded-2xl border border-[#3FBF8F]/20 bg-[#3FBF8F]/[0.07] px-4 py-3 mb-4">
                        <div className="w-8 h-8 rounded-xl bg-[#3FBF8F]/10 flex items-center justify-center">
                          <Gift size={15} className="text-[#3FBF8F]" />
                        </div>

                        <div>
                          <div className="text-[9px] uppercase tracking-wider text-[#6EA88E]">
                            Referral code
                          </div>

                          <div className="text-xs font-semibold text-[#62D0A0] mt-0.5">
                            #{referralCode}
                          </div>
                        </div>
                      </div>
                    )}

                    <ErrorBox error={error} />

                    <form onSubmit={handleSubmit} className="space-y-3">
                      {isRegister && (
                        <InputField
                          icon={UserRound}
                          type="text"
                          placeholder="Full name"
                          value={name}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^A-Za-zÀ-ÿ\s'-]/g,
                              ""
                            );

                            setName(value);
                            setError("");
                          }}
                          autoComplete="name"
                        />
                      )}

                      <InputField
                        icon={Mail}
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setError("");
                        }}
                        autoComplete="email"
                      />

                      <PasswordField
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError("");
                        }}
                        show={showPassword}
                        onToggle={() => setShowPassword((v) => !v)}
                        placeholder="Password"
                        autoComplete={isRegister ? "new-password" : "current-password"}
                      />

                      {isRegister && !referralCode && (
                        <InputField
                          icon={Gift}
                          type="text"
                          inputMode="numeric"
                          placeholder="Referral code (optional)"
                          value={manualReferralCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");

                            setManualReferralCode(value);
                            setError("");
                          }}
                        />
                      )}

                      {!isRegister && (
                        <div className="flex items-center justify-between px-1 pt-1 pb-1">
                          <label className="flex items-center gap-2 text-[11px] text-[#7E829E] cursor-pointer select-none">
                            <span className="relative">
                              <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="peer sr-only"
                              />

                              <span className="flex w-4 h-4 rounded-[5px] border border-white/[0.12] bg-white/[0.03] peer-checked:bg-[#8B7FE8] peer-checked:border-[#8B7FE8] transition-all items-center justify-center">
                                <CheckCircle2
                                  size={10}
                                  className="text-white opacity-0 peer-checked:opacity-100"
                                />
                              </span>
                            </span>

                            Remember me
                          </label>

                          <button
                            type="button"
                            onClick={() => {
                              setShowForgotPassword(true);
                              setForgotEmail(email);
                              setError("");
                            }}
                            className="text-[11px] font-medium text-[#9188EF] hover:text-[#D946A8] transition-colors"
                          >
                            Forgot password?
                          </button>
                        </div>
                      )}

                      <div className="pt-1">
                        <GradientButton disabled={loading}>
                          {loading ? (
                            <>
                              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Please wait...
                            </>
                          ) : isRegister ? (
                            <>
                              <Crown size={16} />
                              Create Account
                              <ArrowRight size={16} />
                            </>
                          ) : (
                            <>
                              Log In
                              <ArrowRight size={16} />
                            </>
                          )}
                        </GradientButton>
                      </div>
                    </form>

                    {/* SECURITY FEATURES */}
                    {isRegister ? (
                      <div className="grid grid-cols-3 gap-2 mt-6 pt-5 border-t border-white/[0.07]">
                        <div className="flex flex-col items-center text-center gap-2">
                          <div className="w-9 h-9 rounded-xl bg-[#8B7FE8]/10 border border-[#8B7FE8]/10 flex items-center justify-center">
                            <ShieldCheck size={15} className="text-[#9D94F7]" />
                          </div>

                          <span className="text-[9px] leading-3 text-[#747895]">
                            Secure
                            <br />
                            & Private
                          </span>
                        </div>

                        <div className="flex flex-col items-center text-center gap-2">
                          <div className="w-9 h-9 rounded-xl bg-[#22D3EE]/10 border border-[#22D3EE]/10 flex items-center justify-center">
                            <Zap size={15} className="text-[#45DDF0]" />
                          </div>

                          <span className="text-[9px] leading-3 text-[#747895]">
                            Fast
                            <br />
                            Verification
                          </span>
                        </div>

                        <div className="flex flex-col items-center text-center gap-2">
                          <div className="w-9 h-9 rounded-xl bg-[#D946A8]/10 border border-[#D946A8]/10 flex items-center justify-center">
                            <Lock size={15} className="text-[#E76EBB]" />
                          </div>

                          <span className="text-[9px] leading-3 text-[#747895]">
                            Your Data
                            <br />
                            Your Control
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2 mt-6 pt-5 border-t border-white/[0.07]">
                        <ShieldCheck size={13} className="text-[#7E83A4]" />

                        <span className="text-[10px] text-[#656A85]">
                          Protected with two-step authentication
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="text-center mt-5">
              <p className="text-[10px] text-[#555A75] leading-5">
                {isRegister
                  ? "Your account is protected with Google Authenticator two-step verification."
                  : "Protected with secure authentication and two-step verification."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}